/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { catchErrorAsJson } from '@fcom/rx';
import { DapiBaseService } from '../dapi-base-service';
import { DapiHttpResponse } from '../dapi-http-response';
import { DapiRequestBuilder } from '../dapi-request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { CheckInAcceptCancelRequest } from '../models/check-in-accept-cancel-request';
import { CheckInBoardingPassByEmailRequest } from '../models/check-in-boarding-pass-by-email-request';
import { CheckInBoardingPassByEmailResponse } from '../models/check-in-boarding-pass-by-email-response';


/**
 * Check-in resources in use
 */
@Injectable({
  providedIn: 'root',
})
export class CheckinService extends DapiBaseService {
  constructor(
    http: HttpClient
  ) {
    super(http);
  }

  /**
   * Path part for operation acceptCheckIn
   */
  static readonly AcceptCheckInPath = '/api/v2/checkin/{orderId}/accept';

  /**
   * acceptCheckIn.
   *
   * Performs check-in for a list of travelerIds and flightIds
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `acceptCheckIn()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  acceptCheckIn$Response(rootUrl: string, params: {
    orderId: string;
    hash?: string;
    body: CheckInAcceptCancelRequest
  }): Observable<DapiHttpResponse<void>> {
    const rb = new DapiRequestBuilder(rootUrl, CheckinService.AcceptCheckInPath, 'post');

    if (params) {
      rb.path('orderId', params.orderId, {});
      rb.query('hash', params.hash, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as DapiHttpResponse<void>;
      }),
      catchErrorAsJson()
    );
  }

  /**
   * acceptCheckIn.
   *
   * Performs check-in for a list of travelerIds and flightIds
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `acceptCheckIn$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  acceptCheckIn(rootUrl: string, params: {
    orderId: string;
    hash?: string;
    body: CheckInAcceptCancelRequest
  }): Observable<void> {
    return this.acceptCheckIn$Response(rootUrl, params).pipe(
      map((r: DapiHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation cancelCheckIn
   */
  static readonly CancelCheckInPath = '/api/v2/checkin/{orderId}/cancel';

  /**
   * cancelCheckIn.
   *
   * Performs cancellation of check-in for a list of travelerIds and flightIds
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `cancelCheckIn()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  cancelCheckIn$Response(rootUrl: string, params: {
    orderId: string;
    hash?: string;
    body: CheckInAcceptCancelRequest
  }): Observable<DapiHttpResponse<void>> {
    const rb = new DapiRequestBuilder(rootUrl, CheckinService.CancelCheckInPath, 'post');

    if (params) {
      rb.path('orderId', params.orderId, {});
      rb.query('hash', params.hash, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as DapiHttpResponse<void>;
      }),
      catchErrorAsJson()
    );
  }

  /**
   * cancelCheckIn.
   *
   * Performs cancellation of check-in for a list of travelerIds and flightIds
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `cancelCheckIn$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  cancelCheckIn(rootUrl: string, params: {
    orderId: string;
    hash?: string;
    body: CheckInAcceptCancelRequest
  }): Observable<void> {
    return this.cancelCheckIn$Response(rootUrl, params).pipe(
      map((r: DapiHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation sendBoardingPass
   */
  static readonly SendBoardingPassPath = '/api/v2/checkin/{orderId}/send-boarding-pass';

  /**
   * sendBoardingPass.
   *
   * Performs sending of a boarding pass for a list of travelerIds and flightIds
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `sendBoardingPass()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  sendBoardingPass$Response(rootUrl: string, params: {
    orderId: string;
    hash?: string;
    body: CheckInBoardingPassByEmailRequest
  }): Observable<DapiHttpResponse<CheckInBoardingPassByEmailResponse>> {
    const rb = new DapiRequestBuilder(rootUrl, CheckinService.SendBoardingPassPath, 'post');

    if (params) {
      rb.path('orderId', params.orderId, {});
      rb.query('hash', params.hash, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as DapiHttpResponse<CheckInBoardingPassByEmailResponse>;
      }),
      catchErrorAsJson()
    );
  }

  /**
   * sendBoardingPass.
   *
   * Performs sending of a boarding pass for a list of travelerIds and flightIds
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `sendBoardingPass$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  sendBoardingPass(rootUrl: string, params: {
    orderId: string;
    hash?: string;
    body: CheckInBoardingPassByEmailRequest
  }): Observable<CheckInBoardingPassByEmailResponse> {
    return this.sendBoardingPass$Response(rootUrl, params).pipe(
      map((r: DapiHttpResponse<CheckInBoardingPassByEmailResponse>) => r.body as CheckInBoardingPassByEmailResponse)
    );
  }

}
